import { Store } from '../store.class'
import { computed } from 'vue'
import i18n from '@/i18n'
import { store } from '..'

import * as Tripply from '@/api/tripply'
import { User, UserSettings, Permissions, Subscription, Plan, Route } from '@/api/tripply/models'

import { authStore } from '@/store/modules/auth.store'
import { DB } from '@/api/tripply/database'
import { timestamp } from '@/api/tripply/tools'
import { Feedback } from '@/api/tripply/models/Feedback'

interface UserState extends Object {
    user: User;
    subscription: Subscription;
    routeLimitExceeded: boolean;
    lastRouteFetch: number | null;
    unsubscribe: {
      user: () => void;
      subscription: () => void;
    };
}

class UserStore extends Store<UserState> {
  constructor() {
    super({
      name: 'user',
      cached: true,
      state: {
        user: User(),
        subscription: Subscription(),
        routeLimitExceeded: false,
        lastRouteFetch: null,
        unsubscribe: {
          user: () => ({}),
          subscription: () => ({})
        }
      }
    })
  }
  onBeforeCreate(state: UserState) {
    state.lastRouteFetch = null
    return state
  }
  onCreated(cachedState: UserState) {
    // If user has changed
    if (cachedState.user.uid !== authStore.getUserId()) {
      this.clearSession()
    }
  }
  
  // Getters/computed
  firstName = this.get(state => state.user && state.user ? (state.user.name || '').split(' ')[0] : '')
  routes = () => computed(() => this.getUserRoutes())
  routeLimitExceeded = () => this.getUserRoutes().length > (this.permission('LIMIT_ROUTES' || 0))
  
  getPlan(): Plan {
    return this.state.subscription.plan
  }

  isOwner (route: string | Route): boolean {
    const userId = authStore.getUserId()   
    if (typeof route == 'string') {
      route = store.route.getRoute(route)
    }
    return Tripply.isRouteOwner(route, userId)
  }
  isDriver(routeId: string): boolean {
    const userId = authStore.getUserId()
    const route = store.route.getRoute(routeId)
    return Tripply.isRouteDriver(route, userId)
  }

  permission(permissionId: keyof Permissions) {
    const plan = this.getPlan()
    return Tripply.permission(plan.id, permissionId)
  }

  async fetchUserRoutes() {
    const userId = authStore.getUserId()   
     
    if (!this.state.lastRouteFetch) {      
      this.mutate(state => state.lastRouteFetch = timestamp())
      const routes = await Tripply.fetchUserRoutes(userId)
      routes.forEach(route => store.route.SET_ROUTE(route.id, route))
      return routes
    }
  }

  getUserRoutes() {
    const userId = authStore.getUserId()
    return store.route.getUserRoutes(userId)
  }

  // Setters
  SET_USER(user: User) {   
    this.mutate(state => state.user = user)
  }
  SET_SUBSCRIPTION(subscription: Subscription) {
    this.mutate(state => state.subscription = subscription)
  }

  // Actions
  async changeUser(userId: string | undefined) {   
    this.clearSubscriptions()          
    if (userId) {      
      this.initSession(userId)      
    } else {
      this.clearSession()
    }
  }

  initSession(userId: string) {
    this.fetchUserRoutes()

    this.state.unsubscribe.subscription = DB.onSubscription(userId, subscription => {
      this.SET_SUBSCRIPTION(subscription)
    })

    this.state.unsubscribe.user = DB.onUser(userId, user => {      
      if (user) {
        user.uid = userId
        user.guest = false
        this.SET_USER(user)
        i18n.global.locale = user.settings.lang
        store.map.setLanguage(user.settings.lang)
        !user.consents.privacy && store.modal.showModal('PrivacyConsent')
      }
    })
  }

  clearSession() {
    this.clearSubscriptions()
    this.SET_USER(User())
    this.SET_SUBSCRIPTION(Subscription())
    this.mutate(state => state.lastRouteFetch = null)
  }

  clearSubscriptions() {
    this.state.unsubscribe.user && this.state.unsubscribe.user()
    this.state.unsubscribe.subscription && this.state.unsubscribe.subscription()
  }

  async changeLang() {
    const userId = store.auth.getUserId()
    if (userId && this.state.user) {
      let newLang = 'da'
      if (this.state.user.settings.lang === newLang) {
        newLang = 'en'
      }
      return DB.updateUser(userId, {'settings.lang': newLang})
    }
  }

  async updateSettings(settings: UserSettings) {
    const userId = store.auth.getUserId()
    if (userId) {
      return DB.updateUser(userId, {'settings': settings})
    }
  }

  async giveFeedback(msg: string) {
    const userId = store.auth.getUserId()
    if (userId) {
      const feedback: Feedback = {
        uid: userId,
        name: this.state.user.name || '',
        mail: this.state.user.email || '',
        msg: msg,
        created: timestamp()
      }
      return Tripply.giveFeedback(feedback)
    }
  }

  acceptPrivacy() {
    const userId = store.auth.getUserId()
    userId && DB.updateUser(userId, {
      'consents.privacy': true
    })
  }

  createRoute() {
    if (store.auth.signedIn()) {
      store.modal.showModal('CreateRoute')
    } else {
      store.modal.showModal('SignIn', { next: () => {
        store.modal.showModal('CreateRoute')
      }})
    }
  }
}

export const userStore = new UserStore()
